import {
  ExhibitorPairStatus,
  IExhibitorPair,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { IGetPaginatedExhibitorPairsQueryReq } from "@evennt-tech/api-v2-external";
import { chunk } from "lodash";
import { apiV2 } from "api/firebase";
import { RequestKey } from "api/request-key";
import getNextPageParam from "tools/pagination/getNextPageParam";

export const EXHIBITOR_PAIRS_PAGE_SIZE = 50;
export const EXHIBITOR_PAIRS_MAX_PAGE_SIZE = 100;
const IDS_CHUNK_SIZE = 50;

export const EXHIBITOR_PAIR_REALTIME_KEYS = [
  NotificationType.CREATED_EXHIBITOR_PAIR,
  NotificationType.UPDATED_EXHIBITOR_PAIR,
  NotificationType.UPDATED_STAND,
  NotificationType.BULK_UPDATED_STANDS,
];

export const exhibitorPairsQueryKey = (showId?: string) => [
  RequestKey.GetExhibitorPairsPaginatedApiV2,
  showId,
];

const fetchAllPages = async (
  params: Omit<IGetPaginatedExhibitorPairsQueryReq, "page">
): Promise<IExhibitorPair[]> => {
  const items: IExhibitorPair[] = [];
  let page: number | undefined = 1;

  while (page) {
    const result = await apiV2.exhibitor.pair.getPaginated({
      ...params,
      page,
      limit: EXHIBITOR_PAIRS_MAX_PAGE_SIZE,
    });
    items.push(...result.items);
    page = getNextPageParam(result);
  }

  return items;
};

type FetchExhibitorPairsByStandIdsArgs = {
  showId: string;
  standIds?: string[];
  legacyStandIds?: string[];
  status?: ExhibitorPairStatus;
};

export const fetchExhibitorPairsByStandIds = async ({
  showId,
  standIds = [],
  legacyStandIds = [],
  status,
}: FetchExhibitorPairsByStandIdsArgs): Promise<IExhibitorPair[]> => {
  const byStandIds = chunk(standIds, IDS_CHUNK_SIZE).map((ids) =>
    fetchAllPages({ showId, status, standIds: ids })
  );
  const byLegacyStandIds = chunk(legacyStandIds, IDS_CHUNK_SIZE).map((ids) =>
    fetchAllPages({ showId, status, legacyStandIds: ids })
  );
  const pages = await Promise.all([...byStandIds, ...byLegacyStandIds]);

  return pages.flat();
};

type FindRegisteredExhibitorPairArgs = {
  showId: string;
  standName: string;
  standSuffixName: string;
  companyName: string;
  companySuffixName: string;
  email: string;
};

export const findRegisteredExhibitorPair = async ({
  showId,
  standName,
  standSuffixName,
  companyName,
  companySuffixName,
  email,
}: FindRegisteredExhibitorPairArgs): Promise<IExhibitorPair | null> => {
  const { items } = await apiV2.exhibitor.pair.getPaginated({
    showId,
    exactStandName: standName,
    exactStandSuffixName: standSuffixName,
    exactCompanyName: companyName,
    exactCompanySuffixName: companySuffixName,
    email,
    limit: 1,
  });

  return items[0] ?? null;
};
